/**
 * useUiMode — which shell the user lands in at `/`.
 *
 * `simple` (default) is the simplified landing: 240px rail + one conversation
 * surface. `detailed` is the full navigational app. The choice is persisted in
 * `users.settings.uiMode` server-side and mirrored optimistically into the auth
 * store so the switch is instant.
 */
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getMeQueryKey, updateMeMutation } from '../generated/api/@tanstack/react-query.gen.js';
import { useAuthStore } from '../store/index.js';
import { adaptMutation } from './adapt-mutation.js';

export type UiMode = 'simple' | 'detailed';

export function useUiMode(): { uiMode: UiMode; setUiMode: (mode: UiMode) => void } {
  const uiMode = useAuthStore((s) => s.user?.settings?.uiMode ?? 'simple');
  const updateSettings = useAuthStore((s) => s.updateSettings);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...adaptMutation(updateMeMutation(), (mode: UiMode) => ({ body: { settings: { uiMode: mode } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getMeQueryKey() }),
  });

  const setUiMode = useCallback(
    (mode: UiMode) => {
      updateSettings({ uiMode: mode });
      mutation.mutate(mode);
    },
    [mutation, updateSettings],
  );

  return { uiMode, setUiMode };
}
